import { useEffect, useMemo, useRef, useState } from 'react';

import useDebounce from 'lib/hooks/useDebounce';

import Switch from 'components/shared/Switch/Switch';
import Popover from 'components/shared/Popovers/Popover/Popover';
import Typography from 'components/shared/Typography/Typography';

const getSearchKey = (term, fields) =>
  term ? JSON.stringify([term, Array.isArray(fields) ? [...fields].sort() : null]) : null;

const getSearchFields = (searchConfig, appliedSearch) =>
  (searchConfig?.fields || []).map((f) => ({
    ...f,
    enabled: Array.isArray(appliedSearch?.fields) ? appliedSearch.fields.includes(f.field) : true
  }));

const SearchInput = ({ defaultFilters, filters, changeFilters, popoverPosition }) => {
  const searchConfig = defaultFilters?.search;
  const appliedSearch = filters?.search?.[0]?.values;
  const appliedKey = getSearchKey(appliedSearch?.searchTerm, appliedSearch?.fields);
  const [searchTerm, setSearchTerm] = useState(appliedSearch?.searchTerm || '');
  const [searchFields, setSearchFields] = useState(() =>
    getSearchFields(searchConfig, appliedSearch)
  );
  const syncedKeyRef = useRef(appliedKey);

  const debouncedSearchTerm = useDebounce(searchTerm, 500);

  const enabledFields = useMemo(
    () => searchFields.filter((f) => f.enabled).map((f) => f.field),
    [searchFields]
  );

  useEffect(() => {
    if (filters?.search) return;
    syncedKeyRef.current = null;
    setSearchTerm('');
  }, [filters?.search]);

  useEffect(() => {
    if (!appliedKey || appliedKey === syncedKeyRef.current) return;
    syncedKeyRef.current = appliedKey;
    setSearchTerm(appliedSearch.searchTerm);
    setSearchFields(getSearchFields(searchConfig, appliedSearch));
  }, [appliedKey]);

  useEffect(() => {
    if (!searchConfig || searchTerm !== debouncedSearchTerm) return;
    const nextKey = getSearchKey(debouncedSearchTerm, enabledFields);
    if (nextKey === syncedKeyRef.current) return;
    syncedKeyRef.current = nextKey;

    if (!debouncedSearchTerm) {
      changeFilters((prev) => {
        if (!prev?.search) return prev;
        const { search: _search, ...rest } = prev;
        return rest;
      });
      return;
    }

    changeFilters((prev) => ({
      ...prev,
      search: [
        { type: 'search', values: { searchTerm: debouncedSearchTerm, fields: enabledFields } }
      ]
    }));
  }, [debouncedSearchTerm, enabledFields]);

  const handleToggleField = (field) => {
    setSearchFields((prev) =>
      prev.map((item) => (item.field === field ? { ...item, enabled: !item.enabled } : item))
    );
  };

  if (!searchConfig) return null;

  return (
    <div className="flex h-6 w-36 min-w-36 flex-row items-center gap-1.5 rounded border border-solid border-neutral-200 bg-white py-0 pl-2 pr-1 [&:hover:not(:has([data-headlessui-state=open]))]:bg-neutral-50">
      <input
        type="text"
        placeholder="Search"
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        className="h-full w-full border-none bg-transparent p-0 text-xs outline-none focus:ring-0"
      />
      <Popover
        icon="context-menu-horizontal"
        iconSize={22}
        position={popoverPosition}
        buttonClassName="!max-h-[22px] !min-w-[22px] !p-0 hover:!bg-neutral-75"
        activeClassNames={{
          button: '!bg-neutral-75 !text-neutral-800 [&_svg_path]:!fill-neutral-800'
        }}
        panelClassName="!min-w-[200px]">
        <div className="flex flex-col gap-3 px-3">
          <Typography variant="p" size="small" weight="semibold" color="neutral-800">
            Search in
          </Typography>
          {searchFields.map((item) => (
            <div key={item.field} className="flex items-center justify-between">
              <Typography variant="p" size="small" color="neutral-800">
                {item.label}
              </Typography>
              <Switch checked={item.enabled} onChange={() => handleToggleField(item.field)} />
            </div>
          ))}
        </div>
      </Popover>
    </div>
  );
};

export default SearchInput;
